import React, { useState, useMemo } from 'react';
import {
    ChevronDown,
    ChevronRight,
    Plus,
    Minus,
    Edit,
    AlertTriangle,
    Info,
    CheckCircle,
    Search,
    FolderOpen,
    Layers,
    Grid3X3,
    HelpCircle
} from 'lucide-react';
import { cn } from '@client/lib';


interface FormDiffViewerProps {
    changes: FormDiff[];
    hierarchicalDiffs?: HierarchicalFormDiff[];
}

export const FormDiffViewer: React.FC<FormDiffViewerProps> = ({
    changes = [],
    hierarchicalDiffs = []
}) => {

    const [searchTerm, setSearchTerm] = useState('');
    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

    const levelIcons = {
        section: FolderOpen,
        subsection: Layers,
        questionGroup: Grid3X3,
        question: HelpCircle
    };

    const processedHierarchicalDiffs = useMemo(() => {
        if (hierarchicalDiffs.length > 0) return hierarchicalDiffs;

        // Simple grouping by section
        const grouped = changes.reduce((acc, change) => {
            if (!change.sectionId) return acc;

            const sectionKey = change.sectionId;
            if (!acc[sectionKey]) {
                acc[sectionKey] = {
                    id: sectionKey,
                    level: 'section' as const,
                    sectionId: change.sectionId,
                    sectionTitle: change.sectionTitle || 'Untitled Section',
                    changes: [],
                    totalChanges: 0,
                    changesSummary: { additions: 0, removals: 0, modifications: 0 },
                    highestSeverity: 'low' as const,
                    categories: [],
                    description: '',
                    children: []
                };
            }

            const section = acc[sectionKey];
            section.changes.push(change);
            section.totalChanges++;

            if (change.type === 'ADD') section.changesSummary.additions++;
            else if (change.type === 'REMOVE') section.changesSummary.removals++;
            else section.changesSummary.modifications++;

            const severityOrder = { low: 0, medium: 1, high: 2 };
            if (severityOrder[change.severity] > severityOrder[section.highestSeverity]) {
                section.highestSeverity = change.severity;
            }

            if (!section.categories.includes(change.category)) {
                section.categories.push(change.category);
            }

            return acc;
        }, {} as Record<string, HierarchicalFormDiff>);

        // Generate descriptions
        Object.values(grouped).forEach(section => {
            const { additions, removals, modifications } = section.changesSummary;
            const parts = [];
            if (additions > 0) parts.push(`${additions} added`);
            if (removals > 0) parts.push(`${removals} removed`);
            if (modifications > 0) parts.push(`${modifications} modified`);
            section.description = parts.join(', ');
        });

        return Object.values(grouped).sort((a, b) => b.totalChanges - a.totalChanges);
    }, [changes, hierarchicalDiffs]);

    const filteredDiffs = useMemo(() => {
        if (!searchTerm) return processedHierarchicalDiffs;

        return processedHierarchicalDiffs.filter(diff => {
            const matchesSection = diff.sectionTitle.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesChanges = diff.changes.some(change =>
                change.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (change.questionTitle?.toLowerCase() || '').includes(searchTerm.toLowerCase())
            );
            return matchesSection || matchesChanges;
        }).map(diff => ({
            ...diff,
            changes: diff.changes.filter(change =>
                searchTerm === '' ||
                change.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (change.questionTitle?.toLowerCase() || '').includes(searchTerm.toLowerCase())
            )
        }));
    }, [processedHierarchicalDiffs, searchTerm]);

    const getChangeIcon = (type: string, size = "w-4 h-4") => {
        switch (type) {
            case 'ADD': return <Plus className={`${size} text-green-600`} />;
            case 'REMOVE': return <Minus className={`${size} text-red-600`} />;
            case 'CHANGE': return <Edit className={`${size} text-blue-600`} />;
            default: return <Info className={`${size} text-gray-600`} />;
        }
    };

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case 'high': return 'text-red-600 bg-red-50 border-red-200';
            case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
            case 'low': return 'text-green-600 bg-green-50 border-green-200';
            default: return 'text-gray-600 bg-gray-50 border-gray-200';
        }
    };

    const getSeverityIcon = (severity: string) => {
        switch (severity) {
            case 'high': return <AlertTriangle className="w-4 h-4" />;
            case 'medium': return <Info className="w-4 h-4" />;
            case 'low': return <CheckCircle className="w-4 h-4" />;
            default: return <Info className="w-4 h-4" />;
        }
    };

    const toggleNode = (nodeId: string) => {
        const newSet = new Set(expandedNodes);
        if (newSet.has(nodeId)) {
            newSet.delete(nodeId);
        } else {
            newSet.add(nodeId);
        }
        setExpandedNodes(newSet);
    };

    const formatValue = (value: any) => {
        if (value === null || value === undefined) return 'null';
        if (typeof value === 'boolean') return value.toString();
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    };

    if (processedHierarchicalDiffs.length === 0) {
        return (
            <div>
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Changes</h3>
                    <p className="text-gray-600">The forms are identical.</p>
                </div>
            </div>
        );
    }

    const flat = processedHierarchicalDiffs.flatMap(d => d.changes ?? []);
    const totalStats = {
        total: flat.length,
        additions: flat.filter(d => d.type === 'ADD').length,
        removals: flat.filter(d => d.type === 'REMOVE').length,
        modifications: flat.filter(d => d.type === 'CHANGE').length,
    };

    return (
        <div>
            <div className="mb-6">
                <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{totalStats.total}</span>
                        <span className="text-gray-600">total changes</span>
                    </div>
                    {totalStats.additions > 0 && (
                        <div className="flex items-center gap-1 text-green-600">
                            <Plus className="w-4 h-4" />
                            <span>{totalStats.additions} added</span>
                        </div>
                    )}
                    {totalStats.removals > 0 && (
                        <div className="flex items-center gap-1 text-red-600">
                            <Minus className="w-4 h-4" />
                            <span>{totalStats.removals} removed</span>
                        </div>
                    )}
                    {totalStats.modifications > 0 && (
                        <div className="flex items-center gap-1 text-blue-600">
                            <Edit className="w-4 h-4" />
                            <span>{totalStats.modifications} modified</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="mb-6">
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search changes..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
            </div>

            <div className="space-y-4">
                {filteredDiffs.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <p className="text-gray-600">No changes match your search.</p>
                    </div>
                ) : (
                    filteredDiffs.map((diff) => {
                        const isExpanded = expandedNodes.has(diff.id);
                        const LevelIcon = levelIcons[diff.level];

                        return (
                            <div key={diff.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                <div
                                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                                    onClick={() => toggleNode(diff.id)}
                                >
                                    <div className="flex items-center space-x-3">
                                        <LevelIcon className="w-5 h-5 text-gray-500" />
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-semibold text-gray-900">{diff.sectionTitle}</h3>
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(diff.highestSeverity)}`}>
                                                    {getSeverityIcon(diff.highestSeverity)}
                                                    <span className="ml-1 capitalize">{diff.highestSeverity}</span>
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600">{diff.description}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <span className="text-sm font-medium text-gray-700">
                                            {diff.totalChanges} change{diff.totalChanges !== 1 ? 's' : ''}
                                        </span>
                                        {isExpanded ?
                                            <ChevronDown className="w-5 h-5 text-gray-400" /> :
                                            <ChevronRight className="w-5 h-5 text-gray-400" />
                                        }
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="border-t border-gray-100 bg-gray-50">
                                        <div className="p-4 space-y-3">
                                            {diff.changes.map((change) => (
                                                <div key={change.id} className="bg-white rounded border p-3">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex items-start space-x-3">
                                                            {getChangeIcon(change.type)}
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm text-gray-900 mb-1">
                                                                    {change.description}
                                                                </p>

                                                                <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                                                                    <span>{change.sectionTitle}</span>
                                                                    {change.subsectionTitle && <><span>→</span><span>{change.subsectionTitle}</span></>}
                                                                    {change.questionGroupTitle && <><span>→</span><span>{change.questionGroupTitle}</span></>}
                                                                    {change.questionTitle && <><span>→</span><span>{change.questionTitle}</span></>}
                                                                </div>

                                                                {change.type === 'CHANGE' && (change.oldValue !== undefined || change.newValue !== undefined) && (
                                                                    <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                                                                        {change.oldValue !== undefined && (
                                                                            <div className="text-red-700 mb-1">
                                                                                <span className="font-medium">Before:</span> {formatValue(change.oldValue)}
                                                                            </div>
                                                                        )}
                                                                        {change.newValue !== undefined && (
                                                                            <div className="text-green-700">
                                                                                <span className="font-medium">After:</span> {formatValue(change.newValue)}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>


                                                        <span
                                                            className={cn(
                                                                "px-2 py-1 text-xs font-medium rounded capitalize",
                                                                change.category === "structure" && "bg-purple-100 text-purple-700",
                                                                change.category === "content" && "bg-blue-100 text-blue-700",
                                                                change.category === "validation" && "bg-orange-100 text-orange-700",
                                                                change.category !== "structure" &&
                                                                change.category !== "content" &&
                                                                change.category !== "validation" &&
                                                                "bg-green-100 text-green-700"
                                                            )}
                                                        >
                                                            {change.category}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};